import { Vector2, Bounds, DotState, PatternType, DotEffect } from '../types';
import { Renderer } from '../renderer/Renderer';
import { Vec2, wrapInPlace } from '../utils/math';
import {
  DOT_RADIUS,
  DOT_SPAWN_ANIMATION_DURATION,
  DOT_SPAWN_SCALE_MAX,
  COLOR_DOT,
  COLOR_DOT_SPAWNING,
  COLOR_DOT_FROZEN,
  COLOR_DOT_SHIELDED,
  COLOR_DOT_SPEED_AURA,
  COLOR_DOT_MAGNET_AURA,
  COLOR_DOT_SPLIT_AURA,
  SPEED_BOOST_MULT,
  MAGNET_PULL_FORCE,
  DOT_BABY_RADIUS,
} from '../utils/constants';

const FROZEN_BORDER_MAX_THICKNESS = 8;
const FROZEN_BORDER_COLOR = '#00CCFF';

const SPAWN_INV_DURATION = 1 / DOT_SPAWN_ANIMATION_DURATION;
const SCALE_RANGE = DOT_SPAWN_SCALE_MAX - 1;

export class Dot {
  position: Vec2;
  velocity: Vector2;
  state: DotState;
  patternId: string | null = null;

  private spawnElapsed: number = 0;
  private currentScale: number = DOT_SPAWN_SCALE_MAX;
  readonly radius: number = DOT_RADIUS;

  // Frozen/thaw properties
  private frozenTime: number = 0;
  private readonly thawDuration: number = 6000;
  private readonly preThawWarningTime: number = 500;
  private isZombie: boolean = false;
  private readonly zombieSpeed: number = 50;
  private vibrationOffset: Vector2 = { x: 0, y: 0 };
  private frozenBorderThickness: number = FROZEN_BORDER_MAX_THICKNESS;

  // Boss-applied effects (set/cleared by BossManager)
  private activeEffects: Set<DotEffect> = new Set();
  private isBaby: boolean = false;
  private babyLifespanMs: number = 0;

  constructor(x: number, y: number, patternId: string | null = null) {
    this.position = new Vec2(x, y);
    this.velocity = { x: 0, y: 0 };
    this.state = DotState.SPAWNING;
    this.patternId = patternId;
  }

  skipSpawnAnimation(): void {
    this.state = DotState.ACTIVE;
    this.currentScale = 1;
    this.spawnElapsed = 1 / SPAWN_INV_DURATION;
  }

  /**
   * Convert this dot into a "baby" splitter dot with a lifespan
   */
  makeBaby(): void {
    this.isBaby = true;
    this.babyLifespanMs = 0;
    Object.defineProperty(this, 'radius', {
      value: DOT_BABY_RADIUS,
      writable: false,
      configurable: true,
    });
  }

  update(dt: number, bounds: Bounds, playerPosition?: Vector2): void {
    // Baby dots expire after their lifespan (even if frozen? no, only when active)
    if (this.isBaby && this.state === DotState.ACTIVE) {
      this.babyLifespanMs += dt * 1000;
      // Babies are short-lived
      if (this.babyLifespanMs > 8000) {
        this.state = DotState.DEAD;
        return;
      }
    }

    if (this.state === DotState.SPAWNING) {
      this.spawnElapsed += dt * 1000;
      const progress = this.spawnElapsed * SPAWN_INV_DURATION;

      if (progress >= 1) {
        this.state = DotState.ACTIVE;
        this.currentScale = 1;
      } else {
        this.currentScale = DOT_SPAWN_SCALE_MAX - SCALE_RANGE * progress;
      }
      return;
    }

    if (this.state === DotState.FROZEN) {
      this.frozenTime += dt * 1000;

      const frozenProgress = Math.min(this.frozenTime / this.thawDuration, 1);
      this.frozenBorderThickness = FROZEN_BORDER_MAX_THICKNESS * (1 - frozenProgress);

      const timeUntilThaw = this.thawDuration - this.frozenTime;
      if (timeUntilThaw <= this.preThawWarningTime && timeUntilThaw > 0) {
        const shakeIntensity = 2 * (1 - timeUntilThaw / this.preThawWarningTime);
        this.vibrationOffset.x = (Math.random() - 0.5) * shakeIntensity;
        this.vibrationOffset.y = (Math.random() - 0.5) * shakeIntensity;
      } else {
        this.vibrationOffset.x = 0;
        this.vibrationOffset.y = 0;
      }

      if (this.frozenTime >= this.thawDuration) {
        this.thaw(playerPosition);
      }
      return;
    }

    if (this.state !== DotState.ACTIVE) {
      return;
    }

    // If this is a zombie dot (thawed from frozen), chase the player
    if (this.isZombie && playerPosition) {
      const dx = playerPosition.x - this.position.x;
      const dy = playerPosition.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      let baseSpeed = this.zombieSpeed;
      // Boss SPEED_BOOST buff amplifies zombie speed too
      if (this.hasEffect(DotEffect.SPEED_BOOST)) {
        baseSpeed *= SPEED_BOOST_MULT;
      }

      if (dist > 0) {
        this.velocity.x = (dx / dist) * baseSpeed;
        this.velocity.y = (dy / dist) * baseSpeed;
      }
    } else if (this.hasEffect(DotEffect.MAGNET) && playerPosition) {
      // Magnet lord pulls dots toward player (additive)
      const dx = playerPosition.x - this.position.x;
      const dy = playerPosition.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        this.velocity.x += (dx / dist) * MAGNET_PULL_FORCE * dt;
        this.velocity.y += (dy / dist) * MAGNET_PULL_FORCE * dt;
      }
    }

    // Speed boost: scale velocity (applied to position update only, not stored)
    let vx = this.velocity.x;
    let vy = this.velocity.y;
    if (this.hasEffect(DotEffect.SPEED_BOOST)) {
      // Apply boost only to the positional delta; don't accumulate
      vx *= SPEED_BOOST_MULT;
      vy *= SPEED_BOOST_MULT;
    }

    this.position.x += vx * dt;
    this.position.y += vy * dt;

    // Don't wrap Cyclone, ClockSweep, SweeperLine, or baby dots
    if (this.patternId !== PatternType.CYCLONE &&
        this.patternId !== PatternType.CLOCK_SWEEP &&
        this.patternId !== PatternType.SWEEPER_LINE &&
        !this.isBaby) {
      wrapInPlace(this.position, bounds);
    }
  }

  render(renderer: Renderer): void {
    const currentRadius = this.radius * this.currentScale;

    // Draw aura for buffed dots (under the dot)
    if (this.state === DotState.ACTIVE) {
      if (this.hasEffect(DotEffect.SPEED_BOOST)) {
        renderer.drawCircle(this.position.x, this.position.y, currentRadius * 2.2, COLOR_DOT_SPEED_AURA);
      }
      if (this.hasEffect(DotEffect.MAGNET)) {
        renderer.drawCircle(this.position.x, this.position.y, currentRadius * 2.2, COLOR_DOT_MAGNET_AURA);
      }
      if (this.hasEffect(DotEffect.SPLITTING)) {
        renderer.drawCircle(this.position.x, this.position.y, currentRadius * 2.2, COLOR_DOT_SPLIT_AURA);
      }
    }

    let color: string;
    switch (this.state) {
      case DotState.SPAWNING:
        color = COLOR_DOT_SPAWNING;
        break;
      case DotState.FROZEN:
        color = COLOR_DOT_FROZEN;
        break;
      default:
        color = this.hasEffect(DotEffect.SHIELD) ? COLOR_DOT_SHIELDED : COLOR_DOT;
    }

    const renderX = this.position.x + this.vibrationOffset.x;
    const renderY = this.position.y + this.vibrationOffset.y;

    renderer.drawCircle(
      renderX,
      renderY,
      currentRadius,
      color
    );

    if (this.state === DotState.SPAWNING) {
      const alpha = 1 - this.currentScale / DOT_SPAWN_SCALE_MAX;
      renderer.drawCircleOutline(
        renderX,
        renderY,
        currentRadius * 1.5,
        `rgba(255,102,102,${alpha.toFixed(2)})`
      );
    }

    if (this.state === DotState.FROZEN) {
      renderer.drawCircleOutline(
        renderX,
        renderY,
        currentRadius + this.frozenBorderThickness,
        FROZEN_BORDER_COLOR
      );
      renderer.drawCircle(
        renderX,
        renderY,
        currentRadius,
        color
      );
    }

    // Shield ring (visible on top)
    if (this.hasEffect(DotEffect.SHIELD) && this.state === DotState.ACTIVE) {
      const pulse = 1 + Math.sin(Date.now() * 0.01) * 0.1;
      renderer.drawCircleOutline(
        renderX,
        renderY,
        currentRadius * 1.8 * pulse,
        '#7DD3FC',
        2
      );
    }
  }

  isLethal(): boolean {
    return this.state === DotState.ACTIVE;
  }

  isFrozen(): boolean {
    return this.state === DotState.FROZEN;
  }

  freeze(): void {
    // Shielded dots can't be frozen
    if (this.hasEffect(DotEffect.SHIELD)) return;
    if (this.state === DotState.ACTIVE || this.state === DotState.SPAWNING) {
      this.state = DotState.FROZEN;
      this.velocity.x = 0;
      this.velocity.y = 0;
      this.frozenTime = 0;
      this.frozenBorderThickness = FROZEN_BORDER_MAX_THICKNESS;
    }
  }

  thaw(playerPosition?: Vector2): void {
    if (this.state === DotState.FROZEN) {
      this.state = DotState.ACTIVE;
      this.isZombie = true;
      this.vibrationOffset.x = 0;
      this.vibrationOffset.y = 0;
      if (playerPosition) {
        const dx = playerPosition.x - this.position.x;
        const dy = playerPosition.y - this.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0) {
          this.velocity.x = (dx / dist) * this.zombieSpeed;
          this.velocity.y = (dy / dist) * this.zombieSpeed;
        }
      }
    }
  }

  /**
   * Attempt to kill the dot. Returns true if the dot actually died.
   * Shielded dots survive (return false).
   */
  kill(): void {
    // Shielded dots can't be killed - they bounce off instead
    if (this.hasEffect(DotEffect.SHIELD) && this.state !== DotState.DEAD) {
      // Remove shield on first hit (one-shot protection)
      this.activeEffects.delete(DotEffect.SHIELD);
      return;
    }
    this.state = DotState.DEAD;
  }

  /**
   * Returns true if this dot should split into babies on death.
   * Self-consuming: clears the splitting effect so it can only fire once.
   */
  shouldSplitOnDeath(): boolean {
    if (this.state !== DotState.DEAD) return false;
    if (this.isBaby) return false;
    if (!this.hasEffect(DotEffect.SPLITTING)) return false;
    this.removeEffect(DotEffect.SPLITTING);
    return true;
  }

  isDead(): boolean {
    return this.state === DotState.DEAD;
  }

  // ============================================
  // EFFECT API (used by BossManager)
  // ============================================

  addEffect(effect: DotEffect): void {
    this.activeEffects.add(effect);
  }

  removeEffect(effect: DotEffect): void {
    this.activeEffects.delete(effect);
  }

  hasEffect(effect: DotEffect): boolean {
    return this.activeEffects.has(effect);
  }

  clearEffects(): void {
    this.activeEffects.clear();
  }

  getPosition(): Vector2 {
    return this.position;
  }

  getRadius(): number {
    return this.radius;
  }

  getEffectiveRadius(): number {
    if (this.state === DotState.FROZEN) {
      return this.radius + this.frozenBorderThickness;
    }
    return this.radius;
  }
}
